
const defaultSurfaces = {
  bgMain: '#f7f7fb',
  bgBody: '#f7f7fb',
  bgCard: '#ffffff',
  textMain: '#0f172a',
  textMuted: '#475569',
  border: '#e2e8f0',
  inputBg: '#ffffff',
  inputBorder: '#94a3b8',
  shadow: '0 1px 4px rgba(0, 0, 0, 0.05)',
  hero: 'linear-gradient(120deg, #0f172a, #111827)'
};

type ThemeSurface = typeof defaultSurfaces;

type ThemePalette = Record<number, string> & Partial<ThemeSurface> & {
  surface?: Partial<ThemeSurface>;
};

// 5 Preset Themes - Shiny/glossy look with proper contrast
export const THEMES: Record<string, ThemePalette> = {
  // COFFEE - Coffee brown theme (primary)
  coffee: {
    50: '#faf6f3', 100: '#f0e8e1', 200: '#e0d0c5', 300: '#c9ae9a', 400: '#a6846c',
    500: '#8b6347', 600: '#6d4c35', 700: '#5a3e2b', 800: '#4a3223', 900: '#3d291c',
    surface: { 
      bgMain: '#fdfcfb', 
      bgBody: '#fdfcfb', 
      bgCard: '#ffffff', 
      textMain: '#1c1917', 
      textMuted: '#57534e', 
      border: '#e7e5e4', 
      inputBg: '#ffffff',
      inputBorder: '#a8a29e',
      shadow: '0 4px 20px rgba(109, 76, 53, 0.15)', 
      hero: 'linear-gradient(135deg, #3d291c 0%, #6d4c35 50%, #8b6347 100%)' 
    }
  },
  
  // SUNSET - Summer/warm orange colors (shiny)
  sunset: {
    50: '#fff7ed', 100: '#ffedd5', 200: '#fed7aa', 300: '#fdba74', 400: '#fb923c',
    500: '#f97316', 600: '#ea580c', 700: '#c2410c', 800: '#9a3412', 900: '#7c2d12',
    surface: { 
      bgMain: '#fffdfb', 
      bgBody: '#fffdfb', 
      bgCard: '#ffffff', 
      textMain: '#1c1917', 
      textMuted: '#57534e', 
      border: '#fed7aa', 
      inputBg: '#ffffff',
      inputBorder: '#fb923c',
      shadow: '0 4px 20px rgba(249, 115, 22, 0.2)', 
      hero: 'linear-gradient(135deg, #7c2d12 0%, #ea580c 50%, #fb923c 100%)' 
    }
  },
  
  // SLATE - Mid-range neutral (shiny gray)
  slate: {
    50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8',
    500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a',
    surface: { 
      bgMain: '#fafbfc', 
      bgBody: '#fafbfc', 
      bgCard: '#ffffff', 
      textMain: '#0f172a', 
      textMuted: '#475569', 
      border: '#e2e8f0', 
      inputBg: '#ffffff',
      inputBorder: '#94a3b8',
      shadow: '0 4px 20px rgba(15, 23, 42, 0.1)', 
      hero: 'linear-gradient(135deg, #0f172a 0%, #334155 50%, #64748b 100%)' 
    }
  },
  
  // DARK - True black theme with white text (shiny)
  dark: {
    50: '#fafafa', 100: '#f5f5f5', 200: '#e5e5e5', 300: '#d4d4d4', 400: '#a3a3a3',
    500: '#737373', 600: '#525252', 700: '#404040', 800: '#262626', 900: '#171717',
    surface: { 
      bgMain: '#000000', 
      bgBody: '#000000', 
      bgCard: '#0a0a0a', 
      textMain: '#ffffff', 
      textMuted: '#a3a3a3', 
      border: '#262626', 
      inputBg: '#ffffff',
      inputBorder: '#404040',
      shadow: '0 4px 30px rgba(255, 255, 255, 0.05)', 
      hero: 'linear-gradient(135deg, #000000 0%, #171717 50%, #262626 100%)' 
    }
  },
  
  // LIGHT - Pure white theme with black text (shiny)
  light: {
    50: '#ffffff', 100: '#fafafa', 200: '#f5f5f5', 300: '#e5e5e5', 400: '#d4d4d4',
    500: '#a3a3a3', 600: '#737373', 700: '#525252', 800: '#404040', 900: '#171717',
    surface: { 
      bgMain: '#ffffff', 
      bgBody: '#ffffff', 
      bgCard: '#ffffff', 
      textMain: '#000000', 
      textMuted: '#525252', 
      border: '#e5e5e5', 
      inputBg: '#ffffff',
      inputBorder: '#a3a3a3',
      shadow: '0 4px 20px rgba(0, 0, 0, 0.08)', 
      hero: 'linear-gradient(135deg, #171717 0%, #404040 50%, #737373 100%)' 
    }
  }
};

export const applyTheme = (themeName: string) => {
  const theme = THEMES[themeName] || THEMES.slate;
  const palette = (theme as any).palette || theme;
  const flatShadow = '0 1px 4px rgba(0, 0, 0, 0.05)';
  
  const surface = {
    ...defaultSurfaces,
    ...(theme.surface || {}),
    ...(theme.bgMain ? { bgMain: theme.bgMain } : {}),
    ...(theme.bgCard ? { bgCard: theme.bgCard } : {}),
    ...(theme.textMain ? { textMain: theme.textMain } : {}),
    ...(theme.textMuted ? { textMuted: theme.textMuted } : {}),
    ...(theme.border ? { border: theme.border } : {}),
    ...(theme.inputBg ? { inputBg: theme.inputBg } : {}),
    ...(theme.inputBorder ? { inputBorder: theme.inputBorder } : {})
  } as ThemeSurface;

  // FORCE inputs to always have white/light background for visibility
  // This prevents tenants from making dark text boxes
  const inputBg = '#ffffff';
  const inputText = '#0f172a';
  const inputPlaceholder = '#6b7280';
  const inputBorder = surface.inputBorder || surface.border || '#e2e8f0';

  const root = document.documentElement;
  Object.keys(palette).forEach((k) => {
    if (/^\d+$/.test(k)) {
      root.style.setProperty(`--color-primary-${k}`, palette[k as any]);
    }
  });

  root.style.setProperty('--bg-main', surface.bgMain);
  root.style.setProperty('--bg-body', surface.bgBody || surface.bgMain);
  root.style.setProperty('--bg-card', surface.bgCard);
  root.style.setProperty('--text-main', surface.textMain);
  root.style.setProperty('--text-muted', surface.textMuted);
  root.style.setProperty('--border-color', surface.border);
  root.style.setProperty('--input-bg', inputBg);
  root.style.setProperty('--input-border', inputBorder);
  root.style.setProperty('--input-text', inputText);
  root.style.setProperty('--input-placeholder', inputPlaceholder);
  root.style.setProperty('--shadow-elevated', surface.shadow || flatShadow);
  root.style.setProperty('--hero-gradient', surface.hero || defaultSurfaces.hero);
};
